import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaEye, FaFilter, FaDownload, FaSpinner, FaTimes, FaBox, FaTruck, FaCheckCircle, FaTimesCircle, FaCamera, FaComments } from 'react-icons/fa';
import { orderService, sellerLifecycleService } from '../../../../services/apiService';
import { toast } from 'react-toastify';
import DispatchFlowWizard from '../components/DispatchFlowWizard';
import OrderChatPanel from '../../../orderTracking/OrderChatPanel';
import SlaCountdown from '../components/SlaCountdown';
import ExtractionAcceptModal from '../components/ExtractionAcceptModal';
import { TIER_LABELS } from '../../../../utils/availabilityTiers';
import { fraudGateService } from '../../../../services/fraudGateService';

const EXTRACTION_TIERS = ['LIKELY', 'BUYER_REQUESTED'];

const MARKETPLACE_LIFECYCLE = {
  PENDING: { label: 'Accept order', next: 'accept' },
  ACCEPTED: { label: 'Mark packing', next: 'packing' },
  PACKING: { label: 'Upload dispatch photo', next: 'dispatch-photo', requiresPhoto: true },
  DISPATCHED: { label: 'Mark in transit', next: 'in-transit' },
  IN_TRANSIT: { label: 'Mark delivered', next: 'delivered' },
};

const ORDER_STATUSES = [
  'Pending Payment',
  'Confirmed',
  'Processing Item',
  'Packed',
  'Handed to Courier',
  'Shipment In Transit',
  'Delivery Completed',
  'Cancelled',
  'Returned'
];

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [lifecycleUpdating, setLifecycleUpdating] = useState(null);
  const [dispatchPhotoOrder, setDispatchPhotoOrder] = useState(null);
  const [extractionAcceptOrder, setExtractionAcceptOrder] = useState(null);
  const [chatOrderId, setChatOrderId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get('status') || searchParams.get('filter') || 'all';
    return s.toLowerCase() === 'cancelled' ? 'cancelled' : s;
  });

  // Hide scrollbar styles
  const hideScrollbarStyle = `
    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
  `;

  const fetchOrders = async () => {
    try {
      setError(null);
      const response = await orderService.getVendorOrders();
      const data = response?.data ?? response ?? {};
      const ordersList = data.orders ?? [];
      if (Array.isArray(ordersList) && ordersList.length >= 0) {
        const transformedOrders = ordersList.map(order => ({
          id: order._id?.toString() || order.id,
          orderId: order.orderId,
          customer: order.isMarketplaceOrder
            ? 'Buyer'
            : order.shippingAddress?.name || 'Unknown',
          amount: order.vendorAmount || order.total || 0,
          status: order.status || 'Pending',
          date: order.createdAt,
          items: order.vendorItemsCount || order.totalItems || order.items?.length || 0,
          payment: order.paymentStatus || 'Pending',
          trackingState: order.trackingState || null,
          catalogNodeId: order.catalogNodeId || null,
          listingId: order.listingId || null,
          isMarketplaceOrder: order.isMarketplaceOrder || !!order.listingId,
          cancellationReason: order.cancellationReason,
          cancelledDate: order.cancelledDate,
          vendorCancelledItems: order.vendorCancelledItems || [],
          raw: order
        }));
        setOrders(transformedOrders);
        const cancelledCount = transformedOrders.filter(o => (o.status || '').toLowerCase() === 'cancelled').length;
        setSummary(prev => ({ ...prev, cancelled: cancelledCount }));
      }
      if (data.summary && typeof data.summary === 'object') {
        setSummary(prev => ({ ...prev, ...data.summary }));
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to fetch orders');
      toast.error(err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const s = searchParams.get('status') || searchParams.get('filter');
    if (s && s.toLowerCase() === 'cancelled') setStatusFilter('cancelled');
  }, [searchParams]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => fetchOrders(), 10000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get tracking steps based on status
  const getTrackingSteps = (status) => {
    const steps = [
      { label: 'Order Placed', icon: FaCheckCircle, completed: true },
      { label: 'Processing Item', icon: FaBox, completed: ['Processing Item', 'Packed', 'Handed to Courier', 'Shipment In Transit', 'Delivery Completed'].includes(status) },
      { label: 'Packed', icon: FaBox, completed: ['Packed', 'Handed to Courier', 'Shipment In Transit', 'Delivery Completed'].includes(status) },
      { label: 'Handed to Courier', icon: FaTruck, completed: ['Handed to Courier', 'Shipment In Transit', 'Delivery Completed'].includes(status) },
      { label: 'Shipment In Transit', icon: FaTruck, completed: ['Shipment In Transit', 'Delivery Completed'].includes(status) },
      { label: 'Delivery Completed', icon: FaCheckCircle, completed: status === 'Delivery Completed' }
    ];
    return steps;
  };

  const filteredOrders = statusFilter === 'all' || !statusFilter
    ? orders
    : orders.filter(o => (o.status || '').toLowerCase() === (statusFilter === 'cancelled' ? 'cancelled' : statusFilter.toLowerCase()));

  const pendingAcceptanceOrders = orders.filter(
    (o) =>
      o.isMarketplaceOrder &&
      o.payment === 'Paid' &&
      (o.trackingState === 'PENDING' || o.raw?.trackingState === 'PENDING')
  );

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedOrder(null);
  };

  const handleSellerCancel = async (order, type) => {
    const orderId = order?.orderId;
    if (!orderId) return;
    const reason =
      type === 'stock'
        ? window.prompt('Reason for stock error:', 'Part out of stock') || 'Out of stock'
        : window.prompt('Reason for cancellation:', 'Cannot fulfil order') || 'Seller cancelled';
    if (!reason) return;

    try {
      setLifecycleUpdating(orderId);
      const fn =
        type === 'stock'
          ? sellerLifecycleService.reportOutOfStock
          : sellerLifecycleService.cancelOrder;
      const result = await fn(orderId, reason);
      toast.success(
        result?.reassignment?.triggered
          ? 'Order cancelled — buyer notified of replacement offer'
          : 'Order cancelled and buyer refunded'
      );
      await fetchOrders();
      closeDetailModal();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setLifecycleUpdating(null);
    }
  };

  const getMarketplaceStep = (order) => {
    const state = order?.trackingState || order?.raw?.trackingState;
    const base = MARKETPLACE_LIFECYCLE[state];
    if (!base) return null;
    if (state === 'PACKING' && order?.raw?.dispatchPhotoApproved) {
      if (order?.raw?.shippingLabelGeneratedAt || order?.raw?.carrierTracking?.awb) {
        return { label: 'Confirm carrier handoff', next: 'dispatch-flow' };
      }
      return { label: 'Dispatch & label', next: 'dispatch-flow' };
    }
    if (state === 'PACKING' && !order?.raw?.dispatchPhotoApproved) {
      return { label: 'Dispatch & label', next: 'dispatch-flow' };
    }
    return base;
  };

  const getOrderDeadline = (order) =>
    order?.raw?.sellerAcceptanceDeadline ||
    order?.raw?.acceptanceDeadline ||
    null;

  const getOrderTier = (order) => order?.raw?.availabilityTier || null;

  const needsExtractionBeforeAccept = (order) => {
    const tier = getOrderTier(order);
    return EXTRACTION_TIERS.includes(tier) && !order?.raw?.extractionConfirmedAt;
  };

  const performAcceptOrder = async (order, payload = {}) => {
    const orderId = order?.orderId;
    if (!orderId) return;
    try {
      setLifecycleUpdating(orderId);
      const updated = await sellerLifecycleService.acceptOrder(orderId, payload);
      const nextState = updated?.trackingState || updated?.data?.trackingState || 'ACCEPTED';
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === orderId
            ? {
                ...o,
                trackingState: nextState,
                raw: {
                  ...(o.raw || {}),
                  trackingState: nextState,
                  extractionConfirmedAt: updated?.extractionConfirmedAt || o.raw?.extractionConfirmedAt,
                },
              }
            : o
        )
      );
      if (selectedOrder?.orderId === orderId) {
        setSelectedOrder((prev) => ({
          ...prev,
          trackingState: nextState,
          raw: {
            ...(prev.raw || {}),
            trackingState: nextState,
            extractionConfirmedAt: updated?.extractionConfirmedAt || prev.raw?.extractionConfirmedAt,
          },
        }));
      }
      toast.success(
        payload.extractionPhotoUrl ? 'Part extraction confirmed — order accepted' : 'Order accepted'
      );
      setExtractionAcceptOrder(null);
      await fetchOrders();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'EXTRACTION_REQUIRED') {
        setExtractionAcceptOrder(order);
      } else {
        toast.error(err.response?.data?.message || err.message || 'Failed to accept order');
      }
    } finally {
      setLifecycleUpdating(null);
    }
  };

  const handleLifecycleAction = async (order) => {
    const orderId = order?.orderId;
    const step = getMarketplaceStep(order);
    if (!orderId || !step) return;

    if (step.next === 'dispatch-flow') {
      setDispatchPhotoOrder(order);
      return;
    }

    if (step.next === 'dispatch-photo') {
      setDispatchPhotoOrder(order);
      return;
    }

    if (step.next === 'accept' && needsExtractionBeforeAccept(order)) {
      setExtractionAcceptOrder(order);
      return;
    }

    const actions = {
      accept: (id) => sellerLifecycleService.acceptOrder(id),
      packing: sellerLifecycleService.markPacking,
      dispatch: sellerLifecycleService.markDispatched,
      'in-transit': sellerLifecycleService.markInTransit,
      delivered: sellerLifecycleService.markDelivered,
    };
    const fn = actions[step.next];
    if (!fn) return;

    try {
      setLifecycleUpdating(orderId);
      const updated = await fn(orderId);
      const nextState = updated?.trackingState || updated?.data?.trackingState;
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === orderId
            ? { ...o, trackingState: nextState, raw: { ...(o.raw || {}), trackingState: nextState } }
            : o
        )
      );
      if (selectedOrder?.orderId === orderId) {
        setSelectedOrder((prev) => ({
          ...prev,
          trackingState: nextState,
          raw: { ...(prev.raw || {}), trackingState: nextState },
        }));
      }
      toast.success('Order updated');
      await fetchOrders();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'DISPATCH_PHOTO_NOT_APPROVED') {
        setDispatchPhotoOrder(order);
        toast.warn('Upload and verify dispatch photo before handoff');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Failed to update order');
      }
    } finally {
      setLifecycleUpdating(null);
    }
  };

  const handleDispatchFlowComplete = async () => {
    await fetchOrders();
    closeDetailModal();
  };

  const handleStatusChange = async (order, status) => {
    const orderId = order?.id ?? order?.orderId ?? order?._id;
    if (!orderId) {
      toast.error('Invalid order');
      return;
    }
    try {
      setUpdatingOrderId(order.id ?? orderId);
      await orderService.updateOrderStatus(orderId, status);
      setOrders(prev =>
        prev.map(o => (o.id === order.id || o.orderId === order.orderId || o.id === orderId ? { ...o, status, raw: { ...(o.raw || {}), status } } : o))
      );
      if (selectedOrder && (selectedOrder.id === order.id || selectedOrder.orderId === order.orderId)) {
        setSelectedOrder({ ...selectedOrder, status, raw: { ...selectedOrder.raw, status } });
      }
      toast.success(`Order status updated to ${status}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update order status');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{hideScrollbarStyle}</style>
      {/* Orders Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Pending</p>
          <p className="text-2xl font-bold text-orange-600">{summary.pending ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Processing</p>
          <p className="text-2xl font-bold text-yellow-600">{summary.processing ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Shipped</p>
          <p className="text-2xl font-bold text-blue-600">{summary.shipped ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Delivered</p>
          <p className="text-2xl font-bold text-green-600">{summary.delivered ?? 0}</p>
        </div>
        <div
          onClick={() => { setStatusFilter('cancelled'); setSearchParams({ status: 'cancelled' }); }}
          className="bg-white rounded-lg p-4 border border-gray-200 cursor-pointer hover:border-red-300 hover:bg-red-50/50 transition-colors"
        >
          <p className="text-sm text-gray-600 mb-1">Cancelled</p>
          <p className="text-2xl font-bold text-red-600">{summary.cancelled ?? 0}</p>
          <p className="text-xs text-red-600 mt-1">Click to view</p>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {pendingAcceptanceOrders.length > 0 && (
        <div className="mb-6 space-y-3">
          <h3 className="text-lg font-bold text-slate-900">Action required — accept orders</h3>
          {pendingAcceptanceOrders.map((order) => (
            <div
              key={order.orderId}
              className="bg-white rounded-xl border-2 border-emerald-200 p-4 shadow-sm space-y-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-bold text-slate-900">{order.orderId}</p>
                  <p className="text-sm text-slate-600">
                    ₹{(Number(order.amount) || 0).toLocaleString()}
                    {getOrderTier(order) && (
                      <span className="ml-2 text-xs font-semibold text-indigo-700">
                        {TIER_LABELS[getOrderTier(order)]}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleViewOrder(order)}
                  className="text-sm font-semibold text-indigo-600"
                >
                  Details
                </button>
              </div>
              {getOrderDeadline(order) && (
                <SlaCountdown
                  deadline={getOrderDeadline(order)}
                  slaHours={order.raw?.orderAcceptanceSlaHours}
                  tierLabel={getOrderTier(order) ? TIER_LABELS[getOrderTier(order)] : null}
                />
              )}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={lifecycleUpdating === order.orderId}
                  onClick={() => handleLifecycleAction(order)}
                  className="min-h-[48px] rounded-xl bg-emerald-600 text-white font-bold text-sm disabled:opacity-50"
                >
                  Accept
                </button>
                <button
                  type="button"
                  disabled={lifecycleUpdating === order.orderId}
                  onClick={() => handleSellerCancel(order, 'cancel')}
                  className="min-h-[48px] rounded-xl border-2 border-red-400 text-red-700 font-bold text-sm disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-xl font-bold text-gray-900">All Orders</h3>
        <div className="flex gap-2 items-center">
          <select
            value={statusFilter}
            onChange={(e) => {
              const v = e.target.value;
              setStatusFilter(v);
              if (v === 'cancelled') setSearchParams({ status: 'cancelled' });
              else setSearchParams({});
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="Pending Payment">Pending Payment</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Processing Item">Processing</option>
            <option value="Packed">Packed</option>
            <option value="Handed to Courier">Handed to Courier</option>
            <option value="Shipment In Transit">Shipped</option>
            <option value="Delivery Completed">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="Returned">Returned</option>
          </select>
          <button className="btn-outline flex items-center gap-2 text-sm">
            <FaDownload /> Export
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600">No orders found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Order ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Items</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tracking</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Payment</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order, idx) => (
                <tr key={order.id ?? order.orderId ?? idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    {order.orderId ?? '—'}
                    {order.isMarketplaceOrder && (
                      <span className="ml-1 text-[10px] font-bold uppercase text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                        MP
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{order.customer ?? 'Unknown'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">₹{(Number(order.amount) || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{order.items ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {order.trackingState ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full w-fit">
                          {order.trackingState}
                        </span>
                        {order.isMarketplaceOrder &&
                          getOrderTier(order) &&
                          (order.trackingState === 'PENDING' || order.raw?.trackingState === 'PENDING') && (
                          <span className="text-[10px] text-slate-600">
                            {TIER_LABELS[getOrderTier(order)] || getOrderTier(order)}
                          </span>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order, e.target.value)}
                      disabled={updatingOrderId === order.id}
                      className={`min-w-[140px] px-2 py-1 text-xs rounded-full border border-transparent focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-70 ${
                        order.status === 'Delivery Completed' || order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'Shipment In Transit' || order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'Processing Item' || order.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'Cancelled' || order.status === 'Returned' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {ORDER_STATUSES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {updatingOrderId === order.id && (
                      <FaSpinner className="inline-block ml-1 animate-spin text-blue-600" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${order.payment === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {order.payment}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button 
                      onClick={() => handleViewOrder(order)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto hide-scrollbar">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Order Details</h2>
                <p className="text-blue-100 text-sm mt-1">Order ID: {selectedOrder.orderId}</p>
              </div>
              <button
                onClick={closeDetailModal}
                className="text-white hover:bg-blue-700 p-2 rounded-lg transition-colors"
              >
                <FaTimes size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Order Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Customer</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.customer ?? 'Unknown'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Order Date</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.date ? formatDate(selectedOrder.date) : '—'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Amount</p>
                  <p className="font-semibold text-gray-900">₹{(Number(selectedOrder.amount) || 0).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Items</p>
                  <p className="font-semibold text-gray-900">{selectedOrder.items ?? 0}</p>
                </div>
              </div>

              {/* Shipping — marketplace orders: label only, no in-app PII */}
              {selectedOrder.isMarketplaceOrder ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">Buyer delivery info</h3>
                  <p className="text-sm text-gray-600">
                    {selectedOrder.raw?.buyerContactHint ||
                      'Buyer name and address are on the shipping label only.'}
                  </p>
                  {selectedOrder.raw?.shippingLabelAvailable &&
                    selectedOrder.raw?.shippingAddress?.mobileMasked && (
                      <p className="text-sm text-gray-600 mt-2">
                        Phone (masked): {selectedOrder.raw.shippingAddress.mobileMasked}
                      </p>
                    )}
                  {selectedOrder.raw?.shippingAddress?.cityState && (
                    <p className="text-xs text-gray-500 mt-1">
                      Area: {selectedOrder.raw.shippingAddress.cityState}
                      {selectedOrder.raw.shippingAddress.postalCode
                        ? ` — ${selectedOrder.raw.shippingAddress.postalCode}`
                        : ''}
                    </p>
                  )}
                </div>
              ) : (
                selectedOrder.raw?.shippingAddress && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-900 mb-2">Shipping Address</h3>
                    <p className="text-sm text-gray-700">{selectedOrder.raw.shippingAddress.name}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.raw.shippingAddress.address}</p>
                    <p className="text-sm text-gray-600">
                      {selectedOrder.raw.shippingAddress.cityState} - {selectedOrder.raw.shippingAddress.postalCode}
                    </p>
                    {selectedOrder.raw.shippingAddress.mobile && (
                      <p className="text-sm text-gray-600 mt-1">
                        Phone: {selectedOrder.raw.shippingAddress.mobile}
                      </p>
                    )}
                  </div>
                )
              )}

              {/* Order Items (your products in this order) */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Order Items</h3>
                <div className="space-y-2">
                  {(selectedOrder.raw?.vendorItems || selectedOrder.raw?.items || []).map((item, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <img loading="lazy" src={item.imageUrl || 'https://via.placeholder.com/60'} alt={item.name} className="w-14 h-14 object-cover rounded" onError={e => { e.target.src = 'https://via.placeholder.com/60'; }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                        <p className="text-xs text-gray-600">Qty: {item.quantity} × ₹{(item.price || 0).toLocaleString()} = ₹{((item.quantity || 0) * (item.price || 0)).toLocaleString()}</p>
                        {item.partNumber && <p className="text-xs text-gray-500">Part # {item.partNumber}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Tracking Timeline */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Order Tracking</h3>
                <div className="space-y-4">
                  {getTrackingSteps(selectedOrder.status).map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${
                            step.completed ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            <Icon className="text-white text-lg" />
                          </div>
                          {index < getTrackingSteps(selectedOrder.status).length - 1 && (
                            <div className={`w-1 h-16 mt-2 ${
                              step.completed ? 'bg-green-500' : 'bg-gray-300'
                            }`}></div>
                          )}
                        </div>
                        <div className="pt-2">
                          <p className={`font-semibold ${
                            step.completed ? 'text-gray-900' : 'text-gray-400'
                          }`}>
                            {step.label}
                          </p>
                          <p className={`text-sm ${
                            step.completed ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {step.completed ? 'Completed' : 'Pending'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Marketplace lifecycle (catalog-node orders) */}
              {(selectedOrder.trackingState || selectedOrder.raw?.trackingState) &&
                getMarketplaceStep(selectedOrder) && (
                <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 space-y-4">
                  <h3 className="text-sm font-bold text-indigo-900">Marketplace order — seller actions</h3>

                  {(selectedOrder.trackingState === 'PENDING' ||
                    selectedOrder.raw?.trackingState === 'PENDING') &&
                    getOrderDeadline(selectedOrder) && (
                    <SlaCountdown
                      deadline={getOrderDeadline(selectedOrder)}
                      slaHours={selectedOrder.raw?.orderAcceptanceSlaHours}
                      tierLabel={
                        getOrderTier(selectedOrder)
                          ? TIER_LABELS[getOrderTier(selectedOrder)]
                          : null
                      }
                    />
                  )}

                  <p className="text-xs text-indigo-700">
                    Current state:{' '}
                    <strong>{selectedOrder.trackingState || selectedOrder.raw?.trackingState}</strong>
                    {getOrderTier(selectedOrder) && (
                      <span className="ml-2 inline-flex rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-indigo-800">
                        {TIER_LABELS[getOrderTier(selectedOrder)]}
                        {selectedOrder.raw?.onboardingSlaBuffered ? ' · onboarding +50% SLA' : ''}
                      </span>
                    )}
                  </p>

                  {getMarketplaceStep(selectedOrder)?.next === 'accept' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        disabled={lifecycleUpdating === selectedOrder.orderId}
                        onClick={() => handleLifecycleAction(selectedOrder)}
                        className="w-full min-h-[52px] rounded-xl bg-emerald-600 px-6 py-4 text-base font-bold text-white shadow-md hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {lifecycleUpdating === selectedOrder.orderId ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaCheckCircle />
                        )}
                        Accept order
                      </button>
                      <button
                        type="button"
                        disabled={lifecycleUpdating === selectedOrder.orderId}
                        onClick={() => handleSellerCancel(selectedOrder, 'cancel')}
                        className="w-full min-h-[52px] rounded-xl border-2 border-red-400 bg-white px-6 py-4 text-base font-bold text-red-700 hover:bg-red-50 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <FaTimesCircle />
                        Reject / cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={lifecycleUpdating === selectedOrder.orderId}
                      onClick={() => handleLifecycleAction(selectedOrder)}
                      className="w-full min-h-[48px] rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {lifecycleUpdating === selectedOrder.orderId ? (
                        <FaSpinner className="inline animate-spin" />
                      ) : (
                        <>
                          {getMarketplaceStep(selectedOrder)?.next === 'dispatch-photo' && (
                            <FaCamera className="inline mr-1" />
                          )}
                          {getMarketplaceStep(selectedOrder)?.label}
                        </>
                      )}
                    </button>
                  )}

                  {needsExtractionBeforeAccept(selectedOrder) && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      Likely / Buyer-requested: extract the part and upload a post-extraction photo before accepting.
                    </p>
                  )}
                  {selectedOrder.raw?.dispatchPhotoApproved && (
                    <p className="text-xs text-emerald-700 mt-2">Dispatch photo verified (DISPATCH-F1)</p>
                  )}
                  {selectedOrder.raw?.shippingLabelAvailable && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await fraudGateService.downloadShippingLabel(selectedOrder.orderId);
                          toast.success('Shipping label downloaded');
                        } catch (err) {
                          toast.error(
                            err.response?.data?.message || err.message || 'Could not download label'
                          );
                        }
                      }}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
                    >
                      <FaDownload /> Download shipping label
                    </button>
                  )}
                  {selectedOrder.isMarketplaceOrder &&
                    selectedOrder.payment === 'Paid' &&
                    (selectedOrder.raw?.canUseOrderChat ??
                      !['PENDING', 'COMPLETED', 'CANCELLED', 'REASSIGNED'].includes(
                        selectedOrder.trackingState || selectedOrder.raw?.trackingState
                      )) && (
                    <button
                      type="button"
                      onClick={() => setChatOrderId(selectedOrder.orderId)}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-800 hover:bg-indigo-50"
                    >
                      <FaComments /> Chat with buyer
                    </button>
                  )}
                  {['ACCEPTED', 'PACKING'].includes(
                    selectedOrder.trackingState || selectedOrder.raw?.trackingState
                  ) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={lifecycleUpdating === selectedOrder.orderId}
                        onClick={() => handleSellerCancel(selectedOrder, 'cancel')}
                        className="rounded-lg border border-red-300 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
                      >
                        Cancel order (seller fault)
                      </button>
                      <button
                        type="button"
                        disabled={lifecycleUpdating === selectedOrder.orderId}
                        onClick={() => handleSellerCancel(selectedOrder, 'stock')}
                        className="rounded-lg border border-orange-300 bg-white px-3 py-2 text-xs font-semibold text-orange-800 hover:bg-orange-50"
                      >
                        Report out of stock
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Status Badges */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Order Status</p>
                  <span className={`px-3 py-1 text-sm rounded-full font-semibold ${
                    selectedOrder.status === 'Delivered' || selectedOrder.status === 'Delivery Completed' ? 'bg-green-100 text-green-800' :
                    selectedOrder.status === 'Shipped' || selectedOrder.status === 'Shipment In Transit' ? 'bg-blue-100 text-blue-800' :
                    selectedOrder.status === 'Processing' || selectedOrder.status === 'Processing Item' ? 'bg-yellow-100 text-yellow-800' :
                    selectedOrder.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedOrder.status ?? 'Pending'}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">Payment Status</p>
                  <span className={`px-3 py-1 text-sm rounded-full font-semibold ${
                    selectedOrder.payment === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedOrder.payment ?? 'Pending'}
                  </span>
                </div>
              </div>

              {/* Full order cancellation */}
              {(selectedOrder.status === 'Cancelled' || selectedOrder.status === 'cancelled') && (selectedOrder.cancellationReason != null || selectedOrder.cancelledDate != null || selectedOrder.raw?.cancellationReason || selectedOrder.raw?.cancelledDate) && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm font-bold text-red-900 mb-2 flex items-center gap-2">
                    <FaTimesCircle /> Order cancelled
                  </h4>
                  <div className="space-y-2 text-sm">
                    {(selectedOrder.cancellationReason || selectedOrder.raw?.cancellationReason) && (
                      <p><span className="font-semibold text-gray-700">Reason:</span> {selectedOrder.cancellationReason || selectedOrder.raw?.cancellationReason}</p>
                    )}
                    {(selectedOrder.cancelledDate || selectedOrder.raw?.cancelledDate) && (
                      <p><span className="font-semibold text-gray-700">Cancelled on:</span> {formatDate(selectedOrder.cancelledDate || selectedOrder.raw?.cancelledDate)}</p>
                    )}
                  </div>
                </div>
              )}
              {/* Your products cancelled by customer (reason visible to you) */}
              {(selectedOrder.vendorCancelledItems?.length > 0 || selectedOrder.raw?.vendorCancelledItems?.length > 0) && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                    <FaTimesCircle /> Your products cancelled by customer
                  </h4>
                  <div className="space-y-3 text-sm">
                    {(selectedOrder.vendorCancelledItems || selectedOrder.raw?.vendorCancelledItems || []).map((ci, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border border-amber-200">
                        <p className="font-semibold text-gray-900">{ci.name || 'Product'}</p>
                        <p className="text-amber-800 mt-1"><span className="font-semibold">Customer reason:</span> {ci.cancellationReason || '—'}</p>
                        {ci.cancelledDate && <p className="text-gray-600 text-xs mt-1">Cancelled on {formatDate(ci.cancelledDate)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {extractionAcceptOrder && (
        <ExtractionAcceptModal
          order={extractionAcceptOrder}
          onClose={() => setExtractionAcceptOrder(null)}
          onAccepted={(payload) => performAcceptOrder(extractionAcceptOrder, payload)}
        />
      )}
      {dispatchPhotoOrder && (
        <DispatchFlowWizard
          order={dispatchPhotoOrder}
          onClose={() => setDispatchPhotoOrder(null)}
          onComplete={handleDispatchFlowComplete}
        />
      )}
      {chatOrderId && (
        <OrderChatPanel
          orderId={chatOrderId}
          viewerRole="seller"
          onClose={() => setChatOrderId(null)}
        />
      )}
    </div>
  );
};

export default Orders;

